import { TimeRange } from "@nova/component-charts"
import { fetchApiGateway } from "@nova/fetch-api-gateway"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { useAppSelector } from "integrations/redux/hooks"
import { useMemo } from "react"
import useSWR from "swr"

const fetchPlatformMetrics = async ({
    orgId,
    pilotId,
    timeRange,
}: {
    orgId: string
    pilotId: string
    timeRange: TimeRange
}) => {
    return fetchApiGateway("get /org/:orgId/fleet/pilot/:pilotId/platform-metrics/:timeRange", {
        params: {
            orgId,
            pilotId,
            timeRange,
        },
    })
}

export const usePlatformMetrics = ({
    pilotId,
    timeRange,
}: {
    pilotId: string
    timeRange: TimeRange
}) => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const key = orgId ? ["platform-metrics", orgId, pilotId, timeRange] : null

    const { data, error, isLoading, mutate } = useSWR(
        key,
        () => (orgId ? fetchPlatformMetrics({ orgId, pilotId, timeRange }) : undefined),
        {
            keepPreviousData: true,
            revalidateOnFocus: false,
        }
    )

    return {
        total: data?.total,
        byDay: data?.byDay,
        isLoading,
        error,
        mutate: () => mutate(data),
    }
}

const fetchLastOnlineDate = async ({ orgId, pilotId }: { orgId: string; pilotId: string }) => {
    return fetchApiGateway("get /org/:orgId/fleet/pilot/:pilotId/last-online", {
        params: {
            orgId,
            pilotId,
        },
    }).then((r) => {
        if (!r.lastOnlineDateISOString) return
        const lastOnlineDate = new Date(r.lastOnlineDateISOString)
        if (!lastOnlineDate.getTime()) return
        return lastOnlineDate
    })
}

export const useLastOnlineDate = ({ pilotId }: { pilotId: string }) => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const key = orgId ? ["platform-metrics", orgId, pilotId, "last-online"] : null

    const { data, error, isLoading, mutate } = useSWR(
        key,
        () => (orgId ? fetchLastOnlineDate({ orgId, pilotId }) : undefined),
        {
            keepPreviousData: true,
            revalidateOnFocus: false,
        }
    )

    const formattedLastOnlineString = useMemo(() => {
        if (!data) return "--"
        const now = new Date()
        const diffInMs = now.getTime() - data.getTime()
        const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24))

        if (diffInDays === 0) {
            return "Today"
        }

        if (diffInDays <= 7) {
            return `${diffInDays} ${diffInDays === 1 ? "day" : "days"} ago`
        }

        const diffInWeeks = Math.floor(diffInDays / 7)
        if (diffInWeeks <= 4) {
            return `${diffInWeeks} ${diffInWeeks === 1 ? "week" : "weeks"} ago`
        }

        const diffInMonths =
            (now.getFullYear() - data.getFullYear()) * 12 + (now.getMonth() - data.getMonth())

        if (diffInMonths < 12) {
            return `${diffInMonths} ${diffInMonths === 1 ? "month" : "months"} ago`
        }

        if (diffInMonths === 12) {
            return "1 year ago"
        }

        return "1+ year ago"
    }, [data])

    return {
        formattedLastOnlineString,
        isLoading,
        error,
        mutate: () => mutate(data),
    }
}
